import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Button,
  Group,
  Text,
  Divider,
  Checkbox,
  TextInput,
  Loader,
  Alert,
} from "@mantine/core";
import {
  IconFolderPlus,
  IconAlertCircle,
  IconCheck,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleGenericError } from "../../utils/errorHandler";

function AddToListModal({ isOpen, onClose, recruit, onSuccess }) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess, showError } = useToast();
  const [lists, setLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedListIds, setSelectedListIds] = useState([]);
  const [originalListIds, setOriginalListIds] = useState([]); // Track original memberships
  const [isSaving, setIsSaving] = useState(false);
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Fetch user's recruiting lists and check which ones contain this recruit
  const fetchLists = async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      // Fetch all lists
      const listsResponse = await fetch(
        getApiEndpoint("/v1/recruiting/lists"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!listsResponse.ok) {
        showError("Failed to load lists");
        setIsLoading(false);
        return;
      }

      const listsResult = await listsResponse.json();
      const allLists = listsResult.data || [];

      // Filter out system lists (like Cold Leads)
      const userLists = allLists.filter(list => !list.is_default);

      // Show lists immediately (optimistic rendering)
      setLists(userLists);
      setIsLoading(false);

      // Fetch leads for ALL lists in parallel (much faster!)
      const leadsPromises = userLists.map(list =>
        fetch(getApiEndpoint(`/v1/recruiting/lists/${list.id}/leads`), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        })
          .then(res => (res.ok ? res.json() : null))
          .then(result => ({
            listId: list.id,
            leads: result?.data || [],
          }))
          .catch(err => {
            return { listId: list.id, leads: [] };
          })
      );

      // Wait for all list checks to complete in parallel
      const allLeadsResults = await Promise.all(leadsPromises);

      // Find which lists contain this recruit
      const listsContainingRecruit = allLeadsResults
        .filter(result => result.leads.some(lead => lead.id === recruit.id))
        .map(result => result.listId);

      setSelectedListIds(listsContainingRecruit);
      setOriginalListIds(listsContainingRecruit);
    } catch (error) {
      handleGenericError(error, "Failed to load lists");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && recruit) {
      // Reset form state when opening
      setShowCreateNew(false);
      setNewListName("");
      // Fetch lists (will auto-select recruit's current lists)
      fetchLists();
    } else {
      // Clear all state when closing
      setSelectedListIds([]);
      setOriginalListIds([]);
      setShowCreateNew(false);
      setNewListName("");
      setLists([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, recruit]);

  const handleToggleList = listId => {
    const isCurrentlySelected = selectedListIds.includes(listId);
    const newSelection = isCurrentlySelected
      ? selectedListIds.filter(id => id !== listId)
      : [...selectedListIds, listId];

    setSelectedListIds(newSelection);
  };

  const handleCreateNewList = async () => {
    if (!newListName.trim()) {
      showError("Please enter a list name");
      return;
    }

    setIsCreating(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/recruiting/lists"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          name: newListName.trim(),
          lead_ids: [], // Don't add lead yet - let user save manually
        }),
      });

      if (response.ok) {
        const result = await response.json();
        const newList = result.data;

        showSuccess(`List "${newListName}" created!`);

        // Add the new list to the lists array
        setLists(prev => [...prev, newList]);

        // Auto-select the new list
        setSelectedListIds(prev => [...prev, newList.id]);

        // Reset form
        setNewListName("");
        setShowCreateNew(false);
      } else {
        showError("Failed to create list");
      }
    } catch (error) {
      handleGenericError(error, "Error creating list");
    } finally {
      setIsCreating(false);
    }
  };

  const handleSave = async () => {
    // Close modal immediately for instant feedback
    if (onSuccess) onSuccess();
    onClose();

    // Show optimistic success message
    showSuccess("List membership updated!");

    // Perform API calls in background
    setIsSaving(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      // Find lists that changed
      const listsToAdd = selectedListIds.filter(
        id => !originalListIds.includes(id)
      );
      const listsToRemove = originalListIds.filter(
        id => !selectedListIds.includes(id)
      );

      // For each list that needs the recruit added
      for (const listId of listsToAdd) {
        const list = lists.find(l => l.id === listId);
        if (!list) continue;

        // Get current leads in this list and add our recruit
        const response = await fetch(
          getApiEndpoint(`/v1/recruiting/lists/${listId}/leads`),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        let currentLeadIds = [];
        if (response.ok) {
          const result = await response.json();
          currentLeadIds = (result.data || []).map(lead => lead.id);
        }

        // Add our recruit if not already there
        if (!currentLeadIds.includes(recruit.id)) {
          currentLeadIds.push(recruit.id);
        }

        // Update the list with complete lead array (use PUT for updates!)
        const updateResponse = await fetch(
          getApiEndpoint("/v1/recruiting/lists"),
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify({
              list_id: Number(listId),
              name: String(list.name),
              lead_ids: currentLeadIds.map(id => Number(id)),
            }),
          }
        );

        if (!updateResponse.ok) {
          await updateResponse.json().catch(() => ({}));

          throw new Error(`Failed to add to list ${list.name}`);
        }
      }

      // For each list that needs the recruit removed
      for (const listId of listsToRemove) {
        const list = lists.find(l => l.id === listId);
        if (!list) {
          continue;
        }

        // Get current leads in this list
        const response = await fetch(
          getApiEndpoint(`/v1/recruiting/lists/${listId}/leads`),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        let currentLeadIds = [];
        if (response.ok) {
          const result = await response.json();
          currentLeadIds = (result.data || []).map(lead => lead.id);
        }

        // Remove our recruit

        currentLeadIds = currentLeadIds.filter(id => id !== recruit.id);

        // Update the list with new lead array (use PUT for updates!)
        const updatePayload = {
          list_id: Number(listId),
          name: String(list.name),
          lead_ids: currentLeadIds.map(id => Number(id)),
        };

        const updateResponse = await fetch(
          getApiEndpoint("/v1/recruiting/lists"),
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify(updatePayload),
          }
        );

        if (!updateResponse.ok) {
          await updateResponse.json().catch(() => ({}));

          throw new Error(`Failed to remove from list ${list.name}`);
        }
      }

      // Background sync completed successfully - no need to show message again
    } catch (error) {
      handleGenericError(
        error,
        "Failed to update lists - please refresh the page"
      );
      // Optionally trigger a refresh of the data
      if (onSuccess) onSuccess();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Text fw={600} size="lg">
          Add {recruit?.name} to Lists
        </Text>
      }
      size="md"
      centered
    >
      <Stack gap="md">
        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader size="md" />
          </Group>
        ) : (
          <>
            {lists.length === 0 && !showCreateNew ? (
              <Alert icon={<IconAlertCircle size={16} />} color="blue">
                You don't have any lists yet. Create one below!
              </Alert>
            ) : (
              <>
                <Text size="sm" c="dimmed">
                  Select which lists this lead should be in. Uncheck to remove
                  from a list:
                </Text>

                <Stack gap="xs" mah={300} style={{ overflowY: "auto" }}>
                  {lists.map(list => {
                    const isChecked = selectedListIds.includes(list.id);
                    return (
                      <Checkbox
                        key={`list-${list.id}-${isChecked}`}
                        label={
                          <Group gap="xs">
                            <Text size="sm">{list.name}</Text>
                            <Text size="xs" c="dimmed">
                              ({list.lead_count || 0} leads)
                            </Text>
                          </Group>
                        }
                        checked={isChecked}
                        onChange={() => {
                          handleToggleList(list.id);
                        }}
                      />
                    );
                  })}
                </Stack>

                {selectedListIds.length === 0 && (
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    color="yellow"
                    variant="light"
                  >
                    This lead will be removed from all lists
                  </Alert>
                )}
              </>
            )}

            <Divider label="Or create a new list" />

            {showCreateNew ? (
              <Stack gap="sm">
                <TextInput
                  label="New List Name"
                  placeholder="Enter list name..."
                  value={newListName}
                  onChange={e => setNewListName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" && !isCreating) {
                      handleCreateNewList();
                    }
                  }}
                />
                <Group gap="sm">
                  <Button
                    variant="light"
                    onClick={handleCreateNewList}
                    loading={isCreating}
                    leftSection={<IconFolderPlus size={16} />}
                  >
                    Create & Add
                  </Button>
                  <Button
                    variant="subtle"
                    onClick={() => {
                      setShowCreateNew(false);
                      setNewListName("");
                    }}
                    disabled={isCreating}
                  >
                    Cancel
                  </Button>
                </Group>
              </Stack>
            ) : (
              <Button
                variant="light"
                color="orange"
                leftSection={<IconFolderPlus size={16} />}
                onClick={() => setShowCreateNew(true)}
                fullWidth
              >
                Create New List
              </Button>
            )}

            <Divider />

            <Group justify="flex-end" gap="sm">
              <Button
                variant="subtle"
                onClick={onClose}
                disabled={isSaving || isCreating}
              >
                Cancel
              </Button>
              <Button
                color="orange"
                onClick={handleSave}
                loading={isSaving}
                disabled={isCreating}
                leftSection={<IconCheck size={16} />}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

export default AddToListModal;
